import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { BottomNav } from '../components/custom/BottomNav';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { format, isToday } from 'date-fns';
import { 
    ArrowLeft, 
    Plus, 
    CheckCircle, 
    AlertTriangle, 
    ChevronRight,
    CalendarPlus,
    Briefcase,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CalendarPage() {
    const [reminders, setReminders] = useState([]);
    const [cases, setCases] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date());
    
    // Add event dialog
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [newCaseId, setNewCaseId] = useState('');
    const [newNotes, setNewNotes] = useState('');
    const [isCritical, setIsCritical] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { getAuthHeaders } = useAuth();
    const navigate = useNavigate();

    const fetchReminders = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/reminders`, {
                headers: getAuthHeaders()
            });
            setReminders(response.data);
        } catch (error) {
            toast.error('Failed to load reminders');
        } finally {
            setIsLoading(false);
        }
    }, [getAuthHeaders]);

    const fetchCases = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/cases`, {
                headers: getAuthHeaders()
            });
            setCases(response.data.filter(c => c.status !== 'Closed'));
        } catch (error) {
            console.error('Failed to load cases');
        }
    }, [getAuthHeaders]);

    useEffect(() => {
        fetchReminders();
        fetchCases();
    }, [fetchReminders, fetchCases]);

    const handleToggleComplete = async (reminderId, currentStatus) => {
        try {
            await axios.put(`${API}/reminders/${reminderId}`, {
                is_completed: !currentStatus
            }, {
                headers: getAuthHeaders()
            });
            fetchReminders();
            toast.success(currentStatus ? 'Marked incomplete' : 'Marked complete');
        } catch (error) {
            toast.error('Failed to update');
        }
    };

    const handleAddEvent = async () => {
        if (!newTitle.trim() || !newCaseId) {
            toast.error('Title and case are required');
            return;
        }
        setIsSubmitting(true);
        try {
            await axios.post(`${API}/reminders`, {
                case_id: newCaseId,
                title: newTitle,
                due_date: format(selectedDate, 'yyyy-MM-dd'),
                notes: newNotes,
                is_critical: isCritical
            }, {
                headers: getAuthHeaders()
            });
            toast.success('Event added');
            setIsAddOpen(false);
            resetForm();
            fetchReminders();
        } catch (error) {
            toast.error('Failed to add event');
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setNewTitle('');
        setNewCaseId('');
        setNewNotes('');
        setIsCritical(false);
    };

    const openAddEvent = (date) => {
        if (date) setSelectedDate(date);
        setIsAddOpen(true);
    };

    const getDaysUntilDue = (dueDate) => {
        try {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const due = new Date(dueDate);
            due.setHours(0, 0, 0, 0);
            return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
        } catch {
            return null;
        }
    };

    // Create reminder date map for calendar highlighting
    const reminderDates = reminders.reduce((acc, r) => {
        try {
            const dateStr = r.due_date?.split('T')[0];
            if (!acc[dateStr]) acc[dateStr] = [];
            acc[dateStr].push(r);
        } catch {
            // ignore
        }
        return acc;
    }, {});

    // Get reminders for selected date
    const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
    const remindersForDate = reminderDates[selectedDateStr] || [];

    // Calendar modifiers for highlighting dates
    const modifiers = {
        hasReminder: (date) => {
            const dateStr = format(date, 'yyyy-MM-dd');
            return !!reminderDates[dateStr];
        },
        hasOverdue: (date) => {
            const dateStr = format(date, 'yyyy-MM-dd');
            const dayReminders = reminderDates[dateStr] || [];
            return dayReminders.some(r => !r.is_completed && getDaysUntilDue(r.due_date) < 0);
        },
        hasCritical: (date) => {
            const dateStr = format(date, 'yyyy-MM-dd');
            const dayReminders = reminderDates[dateStr] || [];
            return dayReminders.some(r => !r.is_completed && r.is_critical && getDaysUntilDue(r.due_date) >= 0);
        },
    };

    const modifiersStyles = {
        hasReminder: { fontWeight: '600' },
        hasOverdue: { 
            backgroundColor: 'rgb(254, 226, 226)', 
            color: 'rgb(185, 28, 28)', 
            fontWeight: '600'
        },
        hasCritical: { 
            backgroundColor: 'rgb(254, 243, 199)', 
            color: 'rgb(180, 83, 9)', 
            fontWeight: '600'
        },
    };

    // Stats
    const overdueCount = reminders.filter(r => !r.is_completed && getDaysUntilDue(r.due_date) < 0).length;
    const upcomingCount = reminders.filter(r => {
        const days = getDaysUntilDue(r.due_date);
        return !r.is_completed && days >= 0 && days <= 7;
    }).length;

    return (
        <div className="min-h-screen bg-background pb-safe">
            {/* Header */}
            <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/50">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="p-2 -ml-2 hover:bg-secondary rounded-full transition-colors"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </button>
                            <h1 className="font-heading text-2xl font-semibold">Calendar</h1>
                        </div>
                        <button
                            onClick={() => openAddEvent(selectedDate)}
                            className="p-3 bg-primary text-white rounded-full shadow-lg hover:shadow-xl transition-all active:scale-95"
                            data-testid="add-event-btn"
                        >
                            <Plus className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            </header>

            <main className="px-6 py-4 space-y-5">
                {/* Quick Stats */}
                <div className="flex gap-3">
                    <div className={`flex-1 p-3 rounded-2xl flex items-center gap-3 ${overdueCount > 0 ? 'bg-red-50' : 'bg-white'} shadow-soft`}>
                        <AlertTriangle className={`h-5 w-5 ${overdueCount > 0 ? 'text-red-500' : 'text-slate-300'}`} />
                        <div>
                            <span className={`font-display text-xl font-bold ${overdueCount > 0 ? 'text-red-600' : 'text-foreground'}`}>
                                {overdueCount}
                            </span>
                            <p className="text-xs text-muted-foreground">Overdue</p>
                        </div>
                    </div>
                    <div className={`flex-1 p-3 rounded-2xl flex items-center gap-3 ${upcomingCount > 0 ? 'bg-amber-50' : 'bg-white'} shadow-soft`}>
                        <CalendarPlus className={`h-5 w-5 ${upcomingCount > 0 ? 'text-amber-500' : 'text-slate-300'}`} />
                        <div>
                            <span className={`font-display text-xl font-bold ${upcomingCount > 0 ? 'text-amber-600' : 'text-foreground'}`}>
                                {upcomingCount}
                            </span>
                            <p className="text-xs text-muted-foreground">This week</p>
                        </div>
                    </div>
                </div>

                {/* Calendar Card */}
                <div className="bg-white rounded-3xl shadow-soft overflow-hidden">
                    <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                        modifiers={modifiers}
                        modifiersStyles={modifiersStyles}
                        className="w-full"
                        classNames={{
                            months: "flex flex-col w-full",
                            month: "space-y-4 w-full",
                            caption: "flex justify-center pt-4 pb-2 relative items-center",
                            caption_label: "text-base font-heading font-semibold",
                            nav: "space-x-1 flex items-center",
                            nav_button: "h-8 w-8 bg-transparent p-0 hover:bg-slate-100 rounded-full transition-colors inline-flex items-center justify-center",
                            nav_button_previous: "absolute left-3",
                            nav_button_next: "absolute right-3",
                            table: "w-full border-collapse px-3",
                            head_row: "flex w-full justify-around",
                            head_cell: "text-muted-foreground font-medium text-[0.75rem] py-2 w-10 text-center",
                            row: "flex w-full justify-around mt-1",
                            cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 h-10 w-10 flex items-center justify-center",
                            day: "h-8 w-8 p-0 font-normal rounded-full hover:bg-slate-100 transition-colors inline-flex items-center justify-center text-sm",
                            day_selected: "bg-[#1C6EE7] text-white hover:bg-[#1C6EE7] hover:text-white focus:bg-[#1C6EE7] focus:text-white rounded-full",
                            day_today: "bg-blue-100 text-[#1C6EE7] font-semibold rounded-full",
                            day_outside: "text-muted-foreground opacity-50",
                            day_disabled: "text-muted-foreground opacity-50",
                        }}
                        data-testid="calendar-main"
                    />
                    
                    {/* Quick Add Button for Selected Date */}
                    <div className="px-4 pb-4 pt-2 border-t border-slate-100">
                        <button
                            onClick={() => openAddEvent(selectedDate)}
                            className="w-full flex items-center justify-center gap-2 py-3 text-primary font-medium hover:bg-indigo-50 rounded-xl transition-colors"
                            data-testid="add-to-date-btn"
                        >
                            <Plus className="h-4 w-4" />
                            Add event for {isToday(selectedDate) ? 'today' : format(selectedDate, 'MMM d')}
                        </button>
                    </div>
                </div>

                {/* Selected Date Events */}
                <div className="bg-white rounded-3xl shadow-soft overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                        <h3 className="font-heading font-semibold">
                            {isToday(selectedDate) ? 'Today' : format(selectedDate, 'EEEE, MMM d')}
                        </h3>
                        {remindersForDate.length > 0 && (
                            <Badge variant="secondary" className="text-xs">{remindersForDate.length}</Badge>
                        )}
                    </div>
                    
                    {remindersForDate.length > 0 ? (
                        <div className="divide-y divide-slate-100">
                            {remindersForDate.map(reminder => {
                                const days = getDaysUntilDue(reminder.due_date);
                                const isOverdue = days < 0 && !reminder.is_completed;
                                
                                return (
                                    <div
                                        key={reminder.id}
                                        className={`px-4 py-3 flex items-center gap-3 ${
                                            reminder.is_completed ? 'bg-slate-50' : isOverdue ? 'bg-red-50/50' : ''
                                        }`}
                                    >
                                        <button
                                            onClick={() => handleToggleComplete(reminder.id, reminder.is_completed)}
                                            className={`w-6 h-6 min-w-6 min-h-6 rounded-full flex items-center justify-center transition-all shrink-0 border-2 ${
                                                reminder.is_completed
                                                    ? 'bg-emerald-500 border-emerald-500 text-white'
                                                    : 'border-slate-300 hover:border-primary hover:bg-indigo-50'
                                            }`}
                                            data-testid={`toggle-${reminder.id}`}
                                        >
                                            {reminder.is_completed && <CheckCircle className="h-4 w-4" />}
                                        </button>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className={`font-medium text-sm truncate ${reminder.is_completed ? 'line-through text-muted-foreground' : ''}`}>
                                                    {reminder.title}
                                                </p>
                                                {reminder.is_critical && !reminder.is_completed && (
                                                    <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
                                                )}
                                            </div>
                                            <button
                                                onClick={() => navigate(`/case/${reminder.case_id}`)}
                                                className="text-xs text-primary hover:underline truncate block"
                                            >
                                                {reminder.case_name}
                                            </button>
                                        </div>
                                        <button
                                            onClick={() => navigate(`/case/${reminder.case_id}`)}
                                            className="p-2 hover:bg-slate-100 rounded-full transition-colors shrink-0"
                                        >
                                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="px-4 py-6 text-center">
                            <p className="text-sm text-muted-foreground">No events scheduled</p>
                            <button
                                onClick={() => openAddEvent(selectedDate)}
                                className="mt-2 text-sm text-primary font-medium hover:underline"
                            >
                                Add one now
                            </button>
                        </div>
                    )}
                </div>

                {/* Spacer for bottom nav */}
                <div className="h-28" />
            </main>

            <BottomNav onQuickAdd={() => openAddEvent(selectedDate)} />

            {/* Add Event Dialog */}
            <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
                <DialogContent className="max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl flex items-center gap-2">
                            <CalendarPlus className="h-5 w-5 text-primary" />
                            Add Event
                        </DialogTitle>
                        <DialogDescription>
                            Add a deadline or reminder for {format(selectedDate, 'MMMM d, yyyy')}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-2">
                        {/* Case Selection - First for prominence */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Briefcase className="h-4 w-4 text-muted-foreground" />
                                Case *
                            </Label>
                            <Select value={newCaseId} onValueChange={setNewCaseId}>
                                <SelectTrigger className="h-12 rounded-xl" data-testid="event-case-select">
                                    <SelectValue placeholder="Select a case" />
                                </SelectTrigger>
                                <SelectContent>
                                    {cases.map(c => (
                                        <SelectItem key={c.id} value={c.id}>
                                            <div className="flex flex-col">
                                                <span>{c.name}</span>
                                                {c.client_name && (
                                                    <span className="text-xs text-muted-foreground">{c.client_name}</span>
                                                )}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Title */}
                        <div className="space-y-2">
                            <Label>Event Title *</Label>
                            <Input
                                placeholder="e.g., File motion, Client meeting"
                                value={newTitle}
                                onChange={(e) => setNewTitle(e.target.value)}
                                className="h-12 rounded-xl"
                                data-testid="event-title-input"
                            />
                        </div>

                        {/* Notes */}
                        <div className="space-y-2">
                            <Label>Notes (optional)</Label>
                            <Input
                                placeholder="Additional details..."
                                value={newNotes}
                                onChange={(e) => setNewNotes(e.target.value)}
                                className="h-12 rounded-xl"
                            />
                        </div>

                        {/* Critical Toggle */}
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                            <div>
                                <p className="font-medium text-sm">Mark as critical</p>
                                <p className="text-xs text-muted-foreground">Important deadline that cannot be missed</p>
                            </div>
                            <Switch
                                checked={isCritical}
                                onCheckedChange={setIsCritical}
                                data-testid="critical-toggle"
                            />
                        </div>

                        {/* Submit */}
                        <Button
                            onClick={handleAddEvent}
                            disabled={isSubmitting || !newTitle.trim() || !newCaseId}
                            className="w-full h-12 rounded-full"
                            data-testid="submit-event-btn"
                        >
                            {isSubmitting ? 'Adding...' : 'Add Event'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
